import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Anchor,
  Divider,
  Button,
  Group,
  ActionIcon,
  Box,
  List,
  ThemeIcon,
  ScrollArea,
  Affix,
  Transition,
  Alert,
  Badge,
  Card,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowUp,
  IconCheck,
  IconInfoCircle,
  IconExternalLink,
  IconScale,
  IconShield,
  IconUsers,
  IconBook,
  IconCreditCard,
  IconEye,
  IconLock,
  IconAlertTriangle,
  IconMail,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWindowScroll } from "@mantine/hooks";
import dayjs from "dayjs";
import "./styles.scss";
import { AppDomain } from "../../Lib/data";

const TermsAndConditions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scroll, scrollTo] = useWindowScroll();
  const [activeSection, setActiveSection] = useState("introduction");

  const sections = [
    { id: "introduction", title: "Introduction", icon: IconInfoCircle },
    { id: "acceptance", title: "Acceptance of Terms", icon: IconCheck },
    { id: "definitions", title: "Definitions", icon: IconBook },
    { id: "services", title: "Our Services", icon: IconUsers },
    { id: "registration", title: "User Registration", icon: IconLock },
    { id: "payment", title: "Payment Terms", icon: IconCreditCard },
    { id: "intellectual", title: "Intellectual Property", icon: IconShield },
    { id: "conduct", title: "User Conduct", icon: IconScale },
    { id: "privacy", title: "Privacy Policy", icon: IconEye },
    { id: "content", title: "Course Content", icon: IconBook },
    {
      id: "cancellation",
      title: "Cancellation & Refunds",
      icon: IconCreditCard,
    },
    {
      id: "liability",
      title: "Limitation of Liability",
      icon: IconAlertTriangle,
    },
    { id: "termination", title: "Termination", icon: IconExternalLink },
    { id: "contact", title: "Contact Information", icon: IconMail },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="terms-container">
      {/* Hero Section */}
      <Box className="terms-hero">
        <Container size="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Group justify="space-between" align="center" mb="xl">
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate(-1)}
                className="back-button"
              >
                Go Back
              </Button>
              <Badge variant="light" color="violet" size="lg">
                Last Updated: {dayjs().format("MMMM DD, YYYY")}
              </Badge>
            </Group>

            <Stack align="center" gap="md" ta="center">
              <ThemeIcon variant="gradient" gradient={{ from: "violet", to: "blue" }} size={80}>
                <IconScale size={40} />
              </ThemeIcon>
              <Title order={1} className="hero-title">
                Terms and Conditions
              </Title>
              <Text size="xl" c="dimmed" maw={600}>
                Please read these terms and conditions carefully before using Emril Academy's
                educational platform and services.
              </Text>
            </Stack>
          </motion.div>
        </Container>
      </Box>

      <Container size="lg" py="xl">
        <div className="terms-content">
          {/* Navigation Sidebar */}
          <Paper className="terms-nav" withBorder p="md" radius="md">
            <Title order={4} mb="md">
              Table of Contents
            </Title>
            <ScrollArea h={400}>
              <Stack gap="xs">
                {sections.map((section) => (
                  <Button
                    key={section.id}
                    variant={activeSection === section.id ? "light" : "subtle"}
                    color="violet"
                    justify="flex-start"
                    leftSection={<section.icon size={16} />}
                    onClick={() => scrollToSection(section.id)}
                    size="sm"
                    fullWidth
                  >
                    {section.title}
                  </Button>
                ))}
              </Stack>
            </ScrollArea>
          </Paper>

          {/* Main Content */}
          <div className="terms-main">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Introduction */}
              <section id="introduction" className="terms-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="light" color="blue" size="lg">
                      <IconInfoCircle size={20} />
                    </ThemeIcon>
                    <Title order={2}>1. Introduction</Title>
                  </Group>

                  <Stack gap="md">
                    <Text>
                      Welcome to Emril Academy, a premier online educational platform dedicated to
                      providing world-class learning experiences. These Terms and Conditions
                      ("Terms", "Agreement") govern your use of our website, mobile applications,
                      and all related services (collectively, the "Service") operated by Emril
                      Academy ("we", "us", or "our").
                    </Text>

                    <Text>
                      Emril Academy is committed to fostering an environment of academic excellence,
                      professional development, and lifelong learning. Our platform connects
                      students with expert instructors across various disciplines including but not
                      limited to technology, business, healthcare, engineering, and creative arts.
                    </Text>

                    <Alert
                      icon={<IconInfoCircle size="1rem" />}
                      title="Important Notice"
                      color="blue"
                    >
                      By accessing or using our Service, you agree to be bound by these Terms. If
                      you disagree with any part of these terms, then you may not access the
                      Service.
                    </Alert>
                  </Stack>
                </Card>
              </section>

              {/* Acceptance of Terms */}
              <section id="acceptance" className="terms-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="light" color="green" size="lg">
                      <IconCheck size={20} />
                    </ThemeIcon>
                    <Title order={2}>2. Acceptance of Terms</Title>
                  </Group>

                  <Stack gap="md">
                    <Text>
                      By creating an account, accessing, or using any part of Emril Academy's
                      services, you expressly acknowledge that you have read, understood, and agree
                      to be bound by these Terms and Conditions in their entirety. This agreement
                      constitutes a legally binding contract between you and Emril Academy.
                    </Text>

                    <Text>
                      Your continued use of the Service following the posting of any changes to
                      these Terms constitutes acceptance of those changes. We reserve the right to
                      update, change, or replace any part of these Terms and Conditions by posting
                      updates and/or changes to our website.
                    </Text>

                    <List
                      spacing="sm"
                      size="sm"
                      center
                      icon={
                        <ThemeIcon color="green" size={24} radius="xl">
                          <IconCheck size="1rem" />
                        </ThemeIcon>
                      }
                    >
                      <List.Item>You must be at least 16 years old to use our services</List.Item>
                      <List.Item>You agree to provide accurate and complete information</List.Item>
                      <List.Item>You acknowledge our right to modify these terms</List.Item>
                      <List.Item>You understand the binding nature of this agreement</List.Item>
                    </List>
                  </Stack>
                </Card>
              </section>

              {/* Definitions */}
              <section id="definitions" className="terms-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="light" color="orange" size="lg">
                      <IconBook size={20} />
                    </ThemeIcon>
                    <Title order={2}>3. Definitions</Title>
                  </Group>

                  <Stack gap="lg">
                    <Text>For the purposes of these Terms and Conditions:</Text>

                    <div className="definition-grid">
                      <div className="definition-item">
                        <Text fw={600} c="violet">
                          "Service"
                        </Text>
                        <Text size="sm">
                          Refers to the Emril Academy platform, including our website, mobile
                          applications, courses, content, and all related services and features.
                        </Text>
                      </div>

                      <div className="definition-item">
                        <Text fw={600} c="violet">
                          "User" or "Student"
                        </Text>
                        <Text size="sm">
                          Any individual who creates an account, accesses, or uses our Service in
                          any capacity.
                        </Text>
                      </div>

                      <div className="definition-item">
                        <Text fw={600} c="violet">
                          "Instructor"
                        </Text>
                        <Text size="sm">
                          Educational professionals who create and deliver course content on our
                          platform.
                        </Text>
                      </div>

                      <div className="definition-item">
                        <Text fw={600} c="violet">
                          "Content"
                        </Text>
                        <Text size="sm">
                          All educational materials, videos, documents, assessments, and interactive
                          elements available on our platform.
                        </Text>
                      </div>

                      <div className="definition-item">
                        <Text fw={600} c="violet">
                          "Account"
                        </Text>
                        <Text size="sm">
                          Your personal profile and access credentials that allow you to use our
                          Service.
                        </Text>
                      </div>

                      <div className="definition-item">
                        <Text fw={600} c="violet">
                          "Subscription"
                        </Text>
                        <Text size="sm">
                          A paid plan that provides access to premium courses and features for a
                          specified period.
                        </Text>
                      </div>
                    </div>
                  </Stack>
                </Card>
              </section>

              {/* Our Services */}
              <section id="services" className="terms-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="light" color="blue" size="lg">
                      <IconUsers size={20} />
                    </ThemeIcon>
                    <Title order={2}>4. Our Services</Title>
                  </Group>

                  <Stack gap="md">
                    <Text>
                      Emril Academy provides a comprehensive online learning platform featuring:
                    </Text>

                    <div className="services-grid">
                      <Card withBorder p="md" radius="md">
                        <Group gap="sm" mb="xs">
                          <IconBook size={20} color="var(--mantine-color-blue-6)" />
                          <Text fw={600}>Live Interactive Courses</Text>
                        </Group>
                        <Text size="sm" c="dimmed">
                          Real-time classes with expert instructors, interactive discussions, and
                          collaborative learning experiences.
                        </Text>
                      </Card>

                      <Card withBorder p="md" radius="md">
                        <Group gap="sm" mb="xs">
                          <IconCreditCard size={20} color="var(--mantine-color-green-6)" />
                          <Text fw={600}>Flexible Payment Plans</Text>
                        </Group>
                        <Text size="sm" c="dimmed">
                          Multiple subscription options including monthly and annual plans with
                          various access levels.
                        </Text>
                      </Card>

                      <Card withBorder p="md" radius="md">
                        <Group gap="sm" mb="xs">
                          <IconShield size={20} color="var(--mantine-color-violet-6)" />
                          <Text fw={600}>Certification Programs</Text>
                        </Group>
                        <Text size="sm" c="dimmed">
                          Industry-recognized certificates upon successful completion of approved
                          courses and assessments.
                        </Text>
                      </Card>

                      <Card withBorder p="md" radius="md">
                        <Group gap="sm" mb="xs">
                          <IconUsers size={20} color="var(--mantine-color-orange-6)" />
                          <Text fw={600}>Community Features</Text>
                        </Group>
                        <Text size="sm" c="dimmed">
                          Student forums, peer collaboration tools, mentorship programs, and
                          networking opportunities.
                        </Text>
                      </Card>
                    </div>

                    <Text>
                      We continuously evolve our services based on educational best practices,
                      technological advancements, and user feedback to ensure the highest quality
                      learning experience.
                    </Text>
                  </Stack>
                </Card>
              </section>

              {/* User Registration */}
              <section id="registration" className="terms-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="light" color="red" size="lg">
                      <IconLock size={20} />
                    </ThemeIcon>
                    <Title order={2}>5. User Registration and Account Security</Title>
                  </Group>

                  <Stack gap="md">
                    <Text>
                      To access our services, you must create an account by providing accurate,
                      complete, and current information. You are responsible for maintaining the
                      confidentiality of your account credentials and for all activities that occur
                      under your account.
                    </Text>

                    <Title order={4}>Registration Requirements:</Title>
                    <List
                      spacing="xs"
                      size="sm"
                      icon={
                        <ThemeIcon color="red" size={20} radius="xl">
                          <IconCheck size="0.8rem" />
                        </ThemeIcon>
                      }
                    >
                      <List.Item>Must be at least 16 years of age</List.Item>
                      <List.Item>Provide valid email address for verification</List.Item>
                      <List.Item>Create a secure password meeting our security standards</List.Item>
                      <List.Item>Agree to receive essential service communications</List.Item>
                      <List.Item>Provide accurate personal and billing information</List.Item>
                    </List>

                    <Title order={4}>Account Security Responsibilities:</Title>
                    <Text>
                      You agree to immediately notify Emril Academy of any unauthorized use of your
                      account or any other breach of security. We will not be liable for any loss or
                      damage arising from your failure to comply with security requirements.
                    </Text>

                    <Alert
                      icon={<IconAlertTriangle size="1rem" />}
                      title="Security Notice"
                      color="red"
                    >
                      Never share your login credentials with others. Use strong, unique passwords
                      and enable two-factor authentication when available.
                    </Alert>
                  </Stack>
                </Card>
              </section>

              {/* Payment Terms */}
              <section id="payment" className="terms-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="light" color="green" size="lg">
                      <IconCreditCard size={20} />
                    </ThemeIcon>
                    <Title order={2}>6. Payment Terms and Billing</Title>
                  </Group>

                  <Stack gap="md">
                    <Text>
                      Emril Academy offers various subscription plans and individual course
                      purchases. All fees are clearly displayed before purchase and are charged in
                      Nigerian Naira ($) unless otherwise specified.
                    </Text>

                    <Title order={4}>Subscription Plans:</Title>
                    <List spacing="sm" size="sm">
                      <List.Item>
                        <Text fw={600}>Monthly Subscriptions:</Text> Billed monthly on the same date
                        as your initial subscription. Can be cancelled at any time with effect from
                        the next billing cycle.
                      </List.Item>
                      <List.Item>
                        <Text fw={600}>Annual Subscriptions:</Text> Billed annually with significant
                        savings. Refunds prorated for the unused portion if cancelled within 30
                        days.
                      </List.Item>
                      <List.Item>
                        <Text fw={600}>Individual Course Purchases:</Text> One-time payments for
                        lifetime access to specific courses, subject to our platform availability.
                      </List.Item>
                    </List>

                    <Title order={4}>Payment Processing:</Title>
                    <Text>
                      We use secure third-party payment processors including Paystack and Monnify to
                      handle all transactions. Your payment information is encrypted and never
                      stored on our servers. All transactions are subject to verification and fraud
                      prevention measures.
                    </Text>

                    <Title order={4}>Auto-Renewal:</Title>
                    <Text>
                      Subscriptions automatically renew unless cancelled before the next billing
                      cycle. You will receive email notifications prior to renewal. Price changes
                      will be communicated at least 30 days in advance.
                    </Text>

                    <Alert
                      icon={<IconInfoCircle size="1rem" />}
                      title="Billing Information"
                      color="blue"
                    >
                      Keep your billing information up to date to avoid service interruptions.
                      Failed payments may result in temporary account suspension.
                    </Alert>
                  </Stack>
                </Card>
              </section>

              {/* Intellectual Property */}
              <section id="intellectual" className="terms-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="light" color="violet" size="lg">
                      <IconShield size={20} />
                    </ThemeIcon>
                    <Title order={2}>7. Intellectual Property Rights</Title>
                  </Group>

                  <Stack gap="md">
                    <Text>
                      All content, features, and functionality on the Emril Academy platform,
                      including but not limited to text, graphics, logos, icons, images, audio
                      clips, video content, and software, are the exclusive property of Emril
                      Academy or its licensors and are protected by international copyright,
                      trademark, and other intellectual property laws.
                    </Text>

                    <Title order={4}>Our Content Rights:</Title>
                    <List spacing="sm" size="sm">
                      <List.Item>
                        Course materials and curricula developed by Emril Academy
                      </List.Item>
                      <List.Item>
                        Platform technology, algorithms, and user interface design
                      </List.Item>
                      <List.Item>Trademarks, service marks, and brand elements</List.Item>
                      <List.Item>Assessment tools and evaluation methodologies</List.Item>
                      <List.Item>Student progress tracking and analytics systems</List.Item>
                    </List>

                    <Title order={4}>Instructor Content:</Title>
                    <Text>
                      Course content created by instructors remains the intellectual property of the
                      respective instructors. However, by uploading content to our platform,
                      instructors grant Emril Academy a worldwide, non-exclusive license to use,
                      distribute, and display such content for platform operations.
                    </Text>

                    <Title order={4}>User-Generated Content:</Title>
                    <Text>
                      Content you submit, post, or create on our platform (discussions, assignments,
                      project submissions) remains your property. However, you grant us a license to
                      use this content for educational purposes, platform improvement, and
                      promotional activities with your consent.
                    </Text>

                    <Alert
                      icon={<IconAlertTriangle size="1rem" />}
                      title="Copyright Protection"
                      color="orange"
                    >
                      Unauthorized reproduction, distribution, or modification of our content is
                      strictly prohibited and may result in legal action.
                    </Alert>
                  </Stack>
                </Card>
              </section>

              {/* User Conduct */}
              <section id="conduct" className="terms-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="light" color="yellow" size="lg">
                      <IconScale size={20} />
                    </ThemeIcon>
                    <Title order={2}>8. User Conduct and Community Guidelines</Title>
                  </Group>

                  <Stack gap="md">
                    <Text>
                      Emril Academy is committed to maintaining a respectful, inclusive, and
                      productive learning environment. All users are expected to conduct themselves
                      professionally and adhere to our community standards.
                    </Text>

                    <Title order={4}>Acceptable Use:</Title>
                    <List spacing="sm" size="sm">
                      <List.Item>
                        Engage respectfully with instructors and fellow students
                      </List.Item>
                      <List.Item>Use the platform solely for educational purposes</List.Item>
                      <List.Item>
                        Respect intellectual property rights and academic integrity
                      </List.Item>
                      <List.Item>Report suspicious activities or policy violations</List.Item>
                      <List.Item>Maintain professional communication standards</List.Item>
                    </List>

                    <Title order={4}>Prohibited Activities:</Title>
                    <List spacing="sm" size="sm">
                      <List.Item>Harassment, discrimination, or hate speech</List.Item>
                      <List.Item>Sharing inappropriate or offensive content</List.Item>
                      <List.Item>
                        Attempting to hack, disrupt, or compromise platform security
                      </List.Item>
                      <List.Item>Creating multiple accounts to circumvent restrictions</List.Item>
                      <List.Item>Distributing spam, malware, or malicious content</List.Item>
                      <List.Item>Academic dishonesty including plagiarism and cheating</List.Item>
                      <List.Item>Commercial solicitation without authorization</List.Item>
                    </List>

                    <Title order={4}>Consequences of Violations:</Title>
                    <Text>
                      Violations of these guidelines may result in warnings, temporary suspension,
                      permanent account termination, or legal action depending on the severity and
                      frequency of the violation. We reserve the right to remove content and
                      restrict access without prior notice.
                    </Text>

                    <Alert icon={<IconInfoCircle size="1rem" />} title="Reporting" color="blue">
                      If you encounter inappropriate behavior or content, please report it
                      immediately through our support channels. We investigate all reports promptly.
                    </Alert>
                  </Stack>
                </Card>
              </section>

              {/* Contact Information */}
              <section id="contact" className="terms-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="light" color="blue" size="lg">
                      <IconMail size={20} />
                    </ThemeIcon>
                    <Title order={2}>14. Contact Information</Title>
                  </Group>

                  <Stack gap="md">
                    <Text>
                      If you have any questions about these Terms and Conditions, please contact us:
                    </Text>

                    <div className="contact-grid">
                      <Card withBorder p="md" radius="md">
                        <Group gap="sm" mb="xs">
                          <IconMail size={20} color="var(--mantine-color-blue-6)" />
                          <Text fw={600}>Email Support</Text>
                        </Group>
                        <Text size="sm">info@{AppDomain}</Text>
                        <Text size="sm">info@{AppDomain}</Text>
                      </Card>

                      <Card withBorder p="md" radius="md">
                        <Group gap="sm" mb="xs">
                          <IconExternalLink size={20} color="var(--mantine-color-green-6)" />
                          <Text fw={600}>Online</Text>
                        </Group>
                        <Text size="sm">www.{AppDomain}/contact</Text>
                        <Text size="sm">Help Center & Live Chat</Text>
                      </Card>
                    </div>

                    <Text size="sm" c="dimmed">
                      These Terms and Conditions are effective as of{" "}
                      {dayjs().format("MMMM DD, YYYY")}
                      and were last updated on {dayjs().format("MMMM DD, YYYY")}.
                    </Text>
                  </Stack>
                </Card>
              </section>
            </motion.div>
          </div>
        </div>
      </Container>

      {/* Scroll to Top Button */}
      <Affix position={{ bottom: 20, right: 20 }}>
        <Transition transition="slide-up" mounted={scroll.y > 0}>
          {(transitionStyles) => (
            <ActionIcon
              style={transitionStyles}
              onClick={() => scrollTo({ y: 0 })}
              size="lg"
              variant="gradient"
              gradient={{ from: "violet", to: "blue" }}
              radius="xl"
            >
              <IconArrowUp size="1.2rem" />
            </ActionIcon>
          )}
        </Transition>
      </Affix>
    </div>
  );
};

export default TermsAndConditions;
